import React, { useState, useEffect } from "react";
import { useJobs } from "../../jobs/hooks/useJobs";
import { useCustomers } from "../../customers/hooks/useCustomers";
import { useProperties } from "../../properties/hooks/useProperties";
import { useEmployees } from "../../employees/hooks/useEmployees";

import { useRoleAccess } from "../../users/hooks/useRoleAccess";
import {
  getAppConfig,
  DateUtils,
  supabase,
  StringValidator,
  ValidationRules,
  FormValidator,
  BaseValidator,
} from "@cleanpro/common-ui";
import {
  Settings as SettingsIcon,
  Upload,
  Download,
  FileText,
  Users,
  Home,
  UserCheck,
  Calendar,
  AlertCircle,
  CheckCircle,
  X,
  Info,
  Download as DownloadIcon,
} from "lucide-react";

interface ImportResult {
  success: number;
  errors: string[];
  total: number;
}

type ImportType = "customers" | "properties" | "employees" | "jobs";

interface ImportModal {
  isOpen: boolean;
  type: ImportType | null;
  title: string;
}

export default function Settings() {
  const { properties, addProperty } = useProperties();
  const { employees, addEmployee } = useEmployees();
  const { customers, createCustomer } = useCustomers();
  const { jobs } = useJobs();
  const permissions = useRoleAccess();
  const [importModal, setImportModal] = useState<ImportModal>({
    isOpen: false,
    type: null,
    title: "",
  });
  const [importFile, setImportFile] = useState<File | null>(null);
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState<ImportResult | null>(null);

  // System information state
  const [dbConnected, setDbConnected] = useState<boolean | null>(null);
  const appConfig = getAppConfig();

  // Check database connection on component mount
  useEffect(() => {
    const checkConnection = async () => {
      try {
        const { error } = await supabase
          .from("customers")
          .select("count")
          .limit(1);
        setDbConnected(!error);
      } catch (error) {
        console.error("Database health check failed:", error);
        setDbConnected(false);
      }
    };

    checkConnection();
  }, []);

  const downloadTemplate = (type: ImportType) => {
    const templateUrls = {
      customers: "/templates/customers-template.csv",
      properties: "/templates/properties-template.csv",
      employees: "/templates/employees-template.csv",
      jobs: "/templates/jobs-template.csv",
    };

    const link = document.createElement("a");
    link.href = templateUrls[type];
    link.download = `${type}-template.csv`;
    link.click();
  };

  const handleImportClick = (type: ImportType) => {
    const titles = {
      customers: "Import Customers",
      properties: "Import Properties",
      employees: "Import Employees",
      jobs: "Import Jobs",
    };

    setImportModal({ isOpen: true, type, title: titles[type] });
    setImportFile(null);
    setImportResult(null);
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file && file.type === "text/csv") {
      setImportFile(file);
    } else {
      alert("Please select a valid CSV file");
    }
  };

  const parseCSV = (text: string): string[][] => {
    const lines = text.split("\n").filter((line) => line.trim());
    return lines.map((line) => {
      const result: string[] = [];
      let current = "";
      let inQuotes = false;

      for (let i = 0; i < line.length; i++) {
        const char = line[i];

        if (char === '"') {
          inQuotes = !inQuotes;
        } else if (char === "," && !inQuotes) {
          result.push(current.trim());
          current = "";
        } else {
          current += char;
        }
      }

      result.push(current.trim());
      return result;
    });
  };

  // Centralized CSV row validators
  const csvValidators = {
    customers: FormValidator.withFields({
      name: StringValidator.required("Name is required").minLength(
        2,
        "Name must be at least 2 characters"
      ) as unknown as BaseValidator<unknown>,
      email: StringValidator.required("Email is required").email(
        "Please enter a valid email address"
      ) as unknown as BaseValidator<unknown>,
      phone: StringValidator.required("Phone is required").addRule(
        ValidationRules.phone("US", "Please enter a valid phone number")
      ) as unknown as BaseValidator<unknown>,
    }),

    properties: FormValidator.withFields({
      customer: StringValidator.required("Customer ID/Name is required") as unknown as BaseValidator<unknown>,
      type: StringValidator.required("Property type is required").oneOf(
        ["house", "apartment", "condo", "townhouse", "office"],
        "Property type must be one of: house, apartment, condo, townhouse, office"
      ) as unknown as BaseValidator<unknown>,
      address: StringValidator.required("Address is required").minLength(
        5,
        "Please enter a complete address"
      ) as unknown as BaseValidator<unknown>,
    }),

    employees: FormValidator.withFields({
      name: StringValidator.required("Name is required").minLength(
        2,
        "Name must be at least 2 characters"
      ) as unknown as BaseValidator<unknown>,
      email: StringValidator.required("Email is required").email(
        "Please enter a valid email address"
      ) as unknown as BaseValidator<unknown>,
      role: StringValidator.required("Role is required") as unknown as BaseValidator<unknown>,
    }),

    jobs: FormValidator.withFields({
      customer: StringValidator.required("Customer is required") as unknown as BaseValidator<unknown>,
      service: StringValidator.required("Service is required") as unknown as BaseValidator<unknown>,
      date: StringValidator.required("Date is required") as unknown as BaseValidator<unknown>,
    }),
  };

  const validateCsvRow = (
    row: string[],
    headers: string[],
    type: ImportType
  ): { valid: boolean; error?: string } => {
    // Create data object from row and headers
    const rowData: Record<string, string> = {};
    for (let index = 0; index < headers.length; index++) {
      const header = headers[index];
      const cleanHeader = header.toLowerCase().replaceAll(/[^a-z]/g, "");
      let fieldName = "";

      // Map CSV headers to our validation field names
      if (cleanHeader.includes("name")) fieldName = "name";
      else if (cleanHeader.includes("email")) fieldName = "email";
      else if (cleanHeader.includes("phone")) fieldName = "phone";
      else if (cleanHeader.includes("customer")) fieldName = "customer";
      else if (cleanHeader.includes("type")) fieldName = "type";
      else if (cleanHeader.includes("address")) fieldName = "address";
      else if (cleanHeader.includes("role")) fieldName = "role";
      else if (cleanHeader.includes("service")) fieldName = "service";
      else if (cleanHeader.includes("date")) fieldName = "date";

      if (fieldName && row[index]) {
        rowData[fieldName] = row[index].trim();
      }
    }

    const validator = csvValidators[type];
    const result = validator.validate(rowData);

    if (!result.isValid) {
      const firstError = Object.values(result.fields).find(
        (field) => !field.isValid
      );
      return {
        valid: false,
        error: firstError?.errors[0] || "Validation failed",
      };
    }

    return { valid: true };
  };

  const findCustomerByNameOrEmail = (identifier: string) => {
    return customers.find(
      (c) =>
        c.name.toLowerCase() === identifier.toLowerCase() ||
        (typeof c.email === "string" &&
          c.email.toLowerCase() === identifier.toLowerCase())
    );
  };

  const processImport = async () => {
    if (!importFile || !importModal.type) return;

    setImporting(true);
    const text = await importFile.text();
    const rows = parseCSV(text);

    if (rows.length < 2) {
      setImportResult({
        success: 0,
        errors: ["File must contain headers and at least one data row"],
        total: 0,
      });
      setImporting(false);
      return;
    }

    const headers = rows[0].map((h) => h.toLowerCase());
    const dataRows = rows.slice(1);
    const result: ImportResult = {
      success: 0,
      errors: [],
      total: dataRows.length,
    };

    for (let i = 0; i < dataRows.length; i++) {
      const row = dataRows[i];
      const rowNumber = i + 2; // +2 because we start from row 2 (after headers)

      try {
        if (importModal.type === "customers") {
          const validation = validateCsvRow(row, headers, "customers");
          if (!validation.valid) {
            result.errors.push(`Row ${rowNumber}: ${validation.error}`);
            continue;
          }

          const nameIndex = headers.findIndex((h) => h.includes("name"));
          const emailIndex = headers.findIndex((h) => h.includes("email"));
          const phoneIndex = headers.findIndex((h) => h.includes("phone"));
          const addressIndex = headers.findIndex((h) => h.includes("address"));

          await createCustomer({
            name: row[nameIndex],
            email: row[emailIndex],
            phone: row[phoneIndex],
            address: row[addressIndex] || "",
            preferences: "",
          });
        } else if (importModal.type === "properties") {
          const validation = validateCsvRow(row, headers, "properties");
          if (!validation.valid) {
            result.errors.push(`Row ${rowNumber}: ${validation.error}`);
            continue;
          }

          const customerIndex = headers.findIndex((h) =>
            h.includes("customer")
          );
          const typeIndex = headers.findIndex((h) => h.includes("type"));
          const addressIndex = headers.findIndex((h) => h.includes("address"));
          const cityIndex = headers.findIndex((h) => h.includes("city"));
          const stateIndex = headers.findIndex((h) => h.includes("state"));
          const postalIndex = headers.findIndex(
            (h) => h.includes("zip") || h.includes("postal")
          );
          const sqftIndex = headers.findIndex(
            (h) =>
              h.includes("sqft") ||
              h.includes("square") ||
              h.includes("footage")
          );
          const bedroomsIndex = headers.findIndex((h) => h.includes("bedroom"));
          const bathroomsIndex = headers.findIndex((h) =>
            h.includes("bathroom")
          );

          // Find customer by name or email
          const customer = findCustomerByNameOrEmail(row[customerIndex]);
          if (!customer) {
            result.errors.push(
              `Row ${rowNumber}: Customer not found: ${row[customerIndex]}`
            );
            continue;
          }

          // Parse optional numeric fields with defaults that satisfy constraints
          const squareFootage =
            sqftIndex >= 0 && row[sqftIndex]
              ? Math.max(1, Number.parseInt(row[sqftIndex]) || 1000)
              : 1000; // Default 1000 sqft, minimum 1
          const bedrooms =
            bedroomsIndex >= 0 && row[bedroomsIndex]
              ? Math.max(0, Number.parseInt(row[bedroomsIndex]) || 1)
              : 1;
          const bathrooms =
            bathroomsIndex >= 0 && row[bathroomsIndex]
              ? Math.max(1, Number.parseInt(row[bathroomsIndex]) || 1)
              : 1; // Minimum 1 bathroom

          await addProperty({
            customerId: customer.id ?? "",
            name: "",
            propertyType: row[typeIndex].toLowerCase() as
              | "house"
              | "apartment"
              | "condo"
              | "townhouse"
              | "office",
            addressLine1: row[addressIndex],
            addressLine2: "",
            city: row[cityIndex] || "",
            state: row[stateIndex] || "",
            postalCode: row[postalIndex] || "",
            squareFootage: squareFootage,
            bedrooms: bedrooms,
            bathrooms: bathrooms,
            floors: 1,
            accessInstructions: "",
            specialInstructions: "",
            hasPets: false,
            petDetails: "",
            preferredCleaningDay: undefined,
            requestedTimeSlot: undefined,
            isActive: true,
          });
        } else if (importModal.type === "employees") {
          const validation = validateCsvRow(row, headers, "employees");
          if (!validation.valid) {
            result.errors.push(`Row ${rowNumber}: ${validation.error}`);
            continue;
          }

          const nameIndex = headers.findIndex((h) => h.includes("name"));
          const emailIndex = headers.findIndex((h) => h.includes("email"));
          const phoneIndex = headers.findIndex((h) => h.includes("phone"));
          const roleIndex = headers.findIndex((h) => h.includes("role"));
          const rateIndex = headers.findIndex(
            (h) => h.includes("rate") || h.includes("wage")
          );

          await addEmployee({
            name: row[nameIndex],
            email: row[emailIndex],
            phone: row[phoneIndex] || "",
            role: row[roleIndex],
            status: "active",
            hireDate: new Date().toISOString().split("T")[0],
            hourlyRate: rateIndex >= 0 ? Number.parseFloat(row[rateIndex]) || 0 : 0,
            userId: null,
          });
        } else if (importModal.type === "jobs") {
          const validation = validateCsvRow(row, headers, "jobs");
          if (!validation.valid) {
            result.errors.push(`Row ${rowNumber}: ${validation.error}`);
            continue;
          }

          // This is more complex as it requires existing customers, services, and employees
          result.errors.push(
            `Row ${rowNumber}: Job import not yet implemented - requires existing data relationships`
          );
          continue;
        }

        result.success++;
      } catch (error) {
        result.errors.push(
          `Row ${rowNumber}: ${
            error instanceof Error ? error.message : "Unknown error"
          }`
        );
      }
    }

    setImportResult(result);
    setImporting(false);
  };

  const exportData = (type: ImportType) => {
    let data: Array<
      | (typeof customers)[0]
      | (typeof properties)[0]
      | (typeof employees)[0]
      | (typeof jobs)[0]
    > = [];
    let filename = "";
    let headers: string[] = [];

    switch (type) {
      case "customers":
        data = customers;
        filename = "customers.csv";
        headers = [
          "Name",
          "Email",
          "Phone",
          "Address",
          "Total Spent",
          "Rating",
        ];
        break;
      case "properties":
        data = properties;
        filename = "properties.csv";
        headers = [
          "Customer Name",
          "Property Type",
          "Address Line 1",
          "City",
          "State",
          "Postal Code",
          "Square Footage",
        ];
        break;
      case "employees":
        data = employees;
        filename = "employees.csv";
        headers = ["Name", "Email", "Phone", "Role", "Status", "Hourly Rate"];
        break;
      case "jobs":
        data = jobs;
        filename = "jobs.csv";
        headers = [
          "ID",
          "Status",
          "Date/Time",
          "Duration",
          "Price",
          "Notes",
        ];
        break;
    }

    const csvContent = [
      headers.join(","),
      ...data.map((item) => {
        switch (type) {
          case "customers": {
            const customer = item as (typeof customers)[0];
            return [
              customer.name,
              customer.email,
              customer.phone,
              customer.address ?? "",
              customer.totalSpent ?? "",
              customer.rating ?? "",
            ].join(",");
          }
          case "properties": {
            const property = item as (typeof properties)[0];
            return [
              property.customerId ?? "",
              property.propertyType ?? "",
              property.addressLine1 ?? "",
              property.city ?? "",
              property.state ?? "",
              property.postalCode ?? "",
              property.squareFootage ?? "",
            ].join(",");
          }
          case "employees": {
            const employee = item as (typeof employees)[0];
            return [
              employee.name,
              employee.email,
              employee.phone ?? "",
              employee.role,
              employee.status,
              employee.hourlyRate ?? "",
            ].join(",");
          }
          case "jobs": {
            const job = item as (typeof jobs)[0];
            return [
              job.id ?? "",
              job.status ?? "",
              job.date_time ?? "",
              job.duration ?? "",
              job.price ?? "",
              job.notes ?? "",
            ].join(",");
          }
          default:
            return "";
        }
      }),
    ].join("\n");

    const blob = new Blob([csvContent], { type: "text/csv" });
    const url = globalThis.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    a.click();
    globalThis.URL.revokeObjectURL(url);
  };

  const closeModal = () => {
    setImportModal({ isOpen: false, type: null, title: "" });
    setImportFile(null);
    setImportResult(null);
  };

  if (!permissions.canViewDashboard) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <AlertCircle className="h-12 w-12 text-red-500 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900">Access Denied</h3>
          <p className="text-gray-500">
            You don't have permission to access settings.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-3xl font-bold text-gray-900">Settings</h1>
        <SettingsIcon className="h-8 w-8 text-gray-500" />
      </div>

      {/* Data Import/Export Section */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200">
        <div className="p-6 border-b border-gray-200">
          <h2 className="text-xl font-semibold text-gray-900 flex items-center gap-2">
            <FileText className="h-5 w-5" />
            Data Import & Export
          </h2>
          <p className="text-gray-600 mt-1">
            Import or export data for customers, properties, employees, and
            jobs.
          </p>
        </div>

        <div className="p-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {/* Customers */}
            <div className="border border-gray-200 rounded-lg p-4">
              <div className="flex items-center gap-3 mb-3">
                <Users className="h-6 w-6 text-blue-500" />
                <h3 className="font-medium text-gray-900">Customers</h3>
              </div>
              <p className="text-sm text-gray-600 mb-4">
                {customers.length} customers in system
              </p>
              <div className="space-y-2">
                <button
                  onClick={() => handleImportClick("customers")}
                  className="w-full bg-indigo-600 text-white px-3 py-2 rounded-lg hover:bg-blue-700 transition-colors flex items-center justify-center gap-2 text-sm"
                >
                  <Upload className="h-4 w-4" />
                  Import
                </button>
                <button
                  onClick={() => exportData("customers")}
                  className="w-full bg-gray-100 text-gray-700 px-3 py-2 rounded-lg hover:bg-gray-200 transition-colors flex items-center justify-center gap-2 text-sm"
                >
                  <Download className="h-4 w-4" />
                  Export
                </button>
                <button
                  onClick={() => downloadTemplate("customers")}
                  className="w-full bg-blue-50 text-blue-700 px-3 py-2 rounded-lg hover:bg-blue-100 transition-colors flex items-center justify-center gap-2 text-sm"
                >
                  <DownloadIcon className="h-4 w-4" />
                  Template
                </button>
              </div>
            </div>

            {/* Properties */}
            <div className="border border-gray-200 rounded-lg p-4">
              <div className="flex items-center gap-3 mb-3">
                <Home className="h-6 w-6 text-green-500" />
                <h3 className="font-medium text-gray-900">Properties</h3>
              </div>
              <p className="text-sm text-gray-600 mb-4">
                {properties.length} properties in system
              </p>
              <div className="space-y-2">
                <button
                  onClick={() => handleImportClick("properties")}
                  className="w-full bg-green-600 text-white px-3 py-2 rounded-lg hover:bg-green-700 transition-colors flex items-center justify-center gap-2 text-sm"
                >
                  <Upload className="h-4 w-4" />
                  Import
                </button>
                <button
                  onClick={() => exportData("properties")}
                  className="w-full bg-gray-100 text-gray-700 px-3 py-2 rounded-lg hover:bg-gray-200 transition-colors flex items-center justify-center gap-2 text-sm"
                >
                  <Download className="h-4 w-4" />
                  Export
                </button>
                <button
                  onClick={() => downloadTemplate("properties")}
                  className="w-full bg-green-50 text-green-700 px-3 py-2 rounded-lg hover:bg-green-100 transition-colors flex items-center justify-center gap-2 text-sm"
                >
                  <DownloadIcon className="h-4 w-4" />
                  Template
                </button>
              </div>
            </div>

            {/* Employees */}
            <div className="border border-gray-200 rounded-lg p-4">
              <div className="flex items-center gap-3 mb-3">
                <UserCheck className="h-6 w-6 text-purple-500" />
                <h3 className="font-medium text-gray-900">Employees</h3>
              </div>
              <p className="text-sm text-gray-600 mb-4">
                {employees.length} employees in system
              </p>
              <div className="space-y-2">
                <button
                  onClick={() => handleImportClick("employees")}
                  className="w-full bg-purple-600 text-white px-3 py-2 rounded-lg hover:bg-purple-700 transition-colors flex items-center justify-center gap-2 text-sm"
                >
                  <Upload className="h-4 w-4" />
                  Import
                </button>
                <button
                  onClick={() => exportData("employees")}
                  className="w-full bg-gray-100 text-gray-700 px-3 py-2 rounded-lg hover:bg-gray-200 transition-colors flex items-center justify-center gap-2 text-sm"
                >
                  <Download className="h-4 w-4" />
                  Export
                </button>
                <button
                  onClick={() => downloadTemplate("employees")}
                  className="w-full bg-purple-50 text-purple-700 px-3 py-2 rounded-lg hover:bg-purple-100 transition-colors flex items-center justify-center gap-2 text-sm"
                >
                  <DownloadIcon className="h-4 w-4" />
                  Template
                </button>
              </div>
            </div>

            {/* Jobs */}
            <div className="border border-gray-200 rounded-lg p-4">
              <div className="flex items-center gap-3 mb-3">
                <Calendar className="h-6 w-6 text-orange-500" />
                <h3 className="font-medium text-gray-900">Jobs</h3>
              </div>
              <p className="text-sm text-gray-600 mb-4">
                {jobs.length} jobs in system
              </p>
              <div className="space-y-2">
                <button
                  onClick={() => handleImportClick("jobs")}
                  className="w-full bg-orange-600 text-white px-3 py-2 rounded-lg hover:bg-orange-700 transition-colors flex items-center justify-center gap-2 text-sm"
                >
                  <Upload className="h-4 w-4" />
                  Import
                </button>
                <button
                  onClick={() => exportData("jobs")}
                  className="w-full bg-gray-100 text-gray-700 px-3 py-2 rounded-lg hover:bg-gray-200 transition-colors flex items-center justify-center gap-2 text-sm"
                >
                  <Download className="h-4 w-4" />
                  Export
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Import Format Guidelines */}
      <div className="bg-blue-50 border border-blue-200 rounded-xl p-6">
        <div className="flex items-start gap-3">
          <Info className="h-5 w-5 text-indigo-600 mt-0.5 flex-shrink-0" />
          <div>
            <h3 className="font-medium text-blue-900 mb-2">
              CSV Import Format Guidelines
            </h3>
            <div className="space-y-2 text-sm text-blue-800">
              <p>
                <strong>Customers:</strong> Required columns - Name, Email,
                Phone. Optional - Address
              </p>
              <p>
                <strong>Properties:</strong> Required columns - Customer (name
                or email), Type, Address. Optional - City, State, Postal Code,
                Square_Footage, Bedrooms, Bathrooms
              </p>
              <p>
                <strong>Employees:</strong> Required columns - Name, Email,
                Role. Optional - Phone, Rate
              </p>
              <p>
                <strong>Jobs:</strong> Currently export-only. Import requires
                existing customer/service/employee relationships.
              </p>
              <p className="mt-3">
                <strong>Tip:</strong> Click the "Template" buttons above to
                download sample CSV files with the correct format.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Application Settings */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200">
        <div className="p-6 border-b border-gray-200">
          <h2 className="text-xl font-semibold text-gray-900 flex items-center gap-2">
            <SettingsIcon className="h-5 w-5" />
            Application Settings
          </h2>
          <p className="text-gray-600 mt-1">
            Configure global application preferences and behaviors.
          </p>
        </div>

        <div className="p-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="border border-gray-200 rounded-lg p-4">
              <h3 className="font-medium text-gray-900 mb-2">
                System Information
              </h3>
              <div className="space-y-1 text-sm text-gray-600">
                <p>Version: {appConfig.app.version}</p>
                <p>
                  Environment:{" "}
                  {appConfig.app.environment.charAt(0).toUpperCase() +
                    appConfig.app.environment.slice(1)}
                </p>
                <p>
                  Database:{" "}
                  {(() => {
                    if (dbConnected === null) return "Checking...";
                    return dbConnected ? "Connected" : "Disconnected";
                  })()}
                </p>
                <p>
                  Last Updated:{" "}
                  {DateUtils.formatDateLong(appConfig.app.buildTime)}
                </p>
              </div>
            </div>

            <div className="border border-gray-200 rounded-lg p-4">
              <h3 className="font-medium text-gray-900 mb-2">Data Summary</h3>
              <div className="space-y-1 text-sm text-gray-600">
                <p>{customers.length} Customers</p>
                <p>{properties.length} Properties</p>
                <p>{employees.length} Employees</p>
                <p>{jobs.length} Jobs</p>
              </div>
            </div>
          </div>

          {/* Future settings can be added here */}
          <div className="mt-6 p-4 bg-gray-50 rounded-lg">
            <p className="text-sm text-gray-600 text-center">
              Additional configuration options will be available in future
              updates.
            </p>
          </div>
        </div>
      </div>

      {/* Import Modal */}
      {importModal.isOpen && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-bold text-gray-900">
                {importModal.title}
              </h2>
              <button
                onClick={closeModal}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="h-6 w-6" />
              </button>
            </div>

            {importResult ? (
              <div className="space-y-4">
                <div className="flex items-center gap-2">
                  {importResult.errors.length === 0 ? (
                    <CheckCircle className="h-5 w-5 text-green-500" />
                  ) : (
                    <AlertCircle className="h-5 w-5 text-yellow-500" />
                  )}
                  <h3 className="font-medium">Import Complete</h3>
                </div>

                <div className="space-y-2 text-sm">
                  <p>
                    <span className="font-medium text-green-600">
                      {importResult.success}
                    </span>{" "}
                    out of{" "}
                    <span className="font-medium">{importResult.total}</span>{" "}
                    records imported successfully.
                  </p>

                  {importResult.errors.length > 0 && (
                    <div>
                      <p className="font-medium text-red-600 mb-2">
                        Errors ({importResult.errors.length}):
                      </p>
                      <div className="max-h-32 overflow-y-auto bg-red-50 border border-red-200 rounded p-2">
                        {importResult.errors.map((error) => (
                          <p key={error} className="text-red-700 text-xs">
                            {error}
                          </p>
                        ))}
                      </div>
                    </div>
                  )}
                </div>

                <button
                  onClick={closeModal}
                  className="w-full bg-indigo-600 text-white py-2 px-4 rounded-lg hover:bg-blue-700 transition-colors"
                >
                  Close
                </button>
              </div>
            ) : (
              <div className="space-y-4">
                <div>
                  <label htmlFor="csv-file-input" className="block text-sm font-medium text-gray-700 mb-2">
                    Select CSV File
                  </label>
                  <input
                    id="csv-file-input"
                    type="file"
                    accept=".csv"
                    onChange={handleFileChange}
                    className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-medium file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
                  />
                </div>

                <div className="flex space-x-3">
                  <button
                    onClick={processImport}
                    disabled={!importFile || importing}
                    className="flex-1 bg-indigo-600 text-white py-2 px-4 rounded-lg hover:bg-blue-700 transition-colors disabled:bg-gray-300 disabled:cursor-not-allowed"
                  >
                    {importing ? "Importing..." : "Import"}
                  </button>
                  <button
                    onClick={closeModal}
                    className="flex-1 bg-gray-300 text-gray-700 py-2 px-4 rounded-lg hover:bg-gray-400 transition-colors"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
